import PageHeader from "@/components/PageHeader";
import { Sparkles, PackageOpen, Wrench, History, CheckCircle2 } from "lucide-react";

const conditions = [
  {
    icon: Sparkles,
    name: "New",
    accent: "#16A34A",
    short: "Unused products in original condition.",
    long: "Brand-new items that are unused and arrive in their original packaging. Includes all original accessories and documentation where applicable.",
    notes: ["Sealed or in original packaging", "All original accessories", "No prior use"],
  },
  {
    icon: PackageOpen,
    name: "Open Box",
    accent: "#2563EB",
    short: "Packaging may have been opened, but product remains in excellent condition.",
    long: "Items that have been opened but otherwise remain in excellent, like-new condition. Packaging may show minor handling marks.",
    notes: ["Packaging opened", "Excellent product condition", "Verified contents"],
  },
  {
    icon: Wrench,
    name: "Refurbished",
    accent: "#D97706",
    short: "Inspected, tested, and prepared for resale.",
    long: "Products that have been inspected, tested, and prepared for resale. May show minor cosmetic signs of prior use. Functionality is verified.",
    notes: ["Tested for functionality", "Minor cosmetic wear possible", "Prepared for resale"],
  },
  {
    icon: History,
    name: "Used",
    accent: "#4B5563",
    short: "Previously owned products showing normal signs of use.",
    long: "Pre-owned products that are fully functional but show normal signs of prior use such as scuffs, scratches, or general wear.",
    notes: ["Visible signs of use", "Fully functional", "Priced accordingly"],
  },
];

export default function ConditionGuide() {
  return (
    <div data-testid="condition-guide-page">
      <PageHeader
        eyebrow="Condition Guide"
        breadcrumb="Condition Guide"
        title="Understand exactly what condition you’re buying."
        subtitle="We use a clear, consistent grading system across every product category so you know what to expect before you purchase."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24">
          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {conditions.map((c) => (
              <article
                key={c.name}
                data-testid={`condition-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-gray-200 rounded-sm p-7 md:p-8 hover:border-[#0B192C] transition-colors bg-white"
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-sm text-white"
                    style={{ backgroundColor: c.accent }}
                  >
                    <c.icon size={22} />
                  </div>
                  <span
                    className="text-[10.5px] font-semibold uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm border"
                    style={{
                      color: c.accent,
                      borderColor: `${c.accent}40`,
                      backgroundColor: `${c.accent}0F`,
                    }}
                  >
                    Grade
                  </span>
                </div>
                <h2 className="text-[26px] font-bold tracking-tight text-[#0B192C]">
                  {c.name}
                </h2>
                <p className="mt-2 text-[14.5px] text-gray-600 italic">{c.short}</p>
                <p className="mt-4 text-[14.5px] text-gray-700 leading-relaxed">
                  {c.long}
                </p>
                <ul className="mt-5 space-y-2.5 border-t border-gray-100 pt-5">
                  {c.notes.map((n) => (
                    <li
                      key={n}
                      className="flex items-start gap-2.5 text-[13.5px] text-gray-700"
                    >
                      <CheckCircle2 size={16} className="text-[#16A34A] mt-0.5 shrink-0" />
                      <span>{n}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          {/* Comparison table */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold tracking-tight text-[#0B192C] mb-6">
              At-a-Glance Comparison
            </h3>
            <div className="border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-left text-[14px]">
                <thead className="bg-[#0B192C] text-white">
                  <tr>
                    <th className="px-5 py-4 font-semibold uppercase text-[11px] tracking-[0.16em]">
                      Grade
                    </th>
                    <th className="px-5 py-4 font-semibold uppercase text-[11px] tracking-[0.16em]">
                      Packaging
                    </th>
                    <th className="px-5 py-4 font-semibold uppercase text-[11px] tracking-[0.16em]">
                      Cosmetic Condition
                    </th>
                    <th className="px-5 py-4 font-semibold uppercase text-[11px] tracking-[0.16em]">
                      Functional Test
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-semibold text-[#0B192C]">New</td>
                    <td className="px-5 py-4 text-gray-700">Sealed / Original</td>
                    <td className="px-5 py-4 text-gray-700">Pristine</td>
                    <td className="px-5 py-4 text-gray-700">Not required</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-semibold text-[#0B192C]">Open Box</td>
                    <td className="px-5 py-4 text-gray-700">Opened</td>
                    <td className="px-5 py-4 text-gray-700">Excellent</td>
                    <td className="px-5 py-4 text-gray-700">Verified</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-semibold text-[#0B192C]">Refurbished</td>
                    <td className="px-5 py-4 text-gray-700">May vary</td>
                    <td className="px-5 py-4 text-gray-700">Minor wear</td>
                    <td className="px-5 py-4 text-gray-700">Tested</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-5 py-4 font-semibold text-[#0B192C]">Used</td>
                    <td className="px-5 py-4 text-gray-700">May vary</td>
                    <td className="px-5 py-4 text-gray-700">Visible wear</td>
                    <td className="px-5 py-4 text-gray-700">Functional</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[12.5px] text-gray-500">
              Note: Specific condition notes are included on each product
              listing. Customers should review listings carefully before
              purchase.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
